import type { CurriculumApi } from '@mathflat/domain/@entities/Curriculum/api'
import { EMPTY_NUMBER_ID } from 'src/constant/common'
/* eslint-disable no-useless-escape */
/* eslint-disable no-useless-backreference */
import { CheckableColl } from 'src/models/CheckableColl'
import CheckableMap from 'src/models/CheckableMap'

import type { SchoolGradeData } from '@/api/studentHistory.api.ts'
import worksheetApi from '@/api/worksheet.api'
import type { 학습지필터 } from '@/contentComponents/makeLessonMaterial/(filter)/_deprecated/service/MakeLessonMaterialFilter.service'
import type { DownloadWorksheetT } from '@/contentComponents/modal/Download/WorksheetDownload.service'
import type { LessonClass } from '@/dto'
import {
  MakeProblem,
  MakeProblemColl,
  MakeWorksheetFilterOption,
  Worksheet,
  WorksheetColl,
  WorksheetDesign,
  WorksheetDesignOption,
} from '@/dto'
import { RouteName } from '@/router/RouteName'
import { splitFilenameAndExtension } from '@/utils/download'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

class WorksheetController implements Controller {
  adapter = {
    worksheet: makeAdapter({
      from(response: res.MyWorksheet) {
        return new Worksheet(response)
      },
    }),
    worksheetColl: makeAdapter({
      from(response: res.MyWorksheet[] | res.ArithmeticWorksheet[]) {
        const worksheetList = response.map((res) => new Worksheet(res))
        return new CheckableColl(CheckableMap.fromArr(worksheetList, 'id' as keyof Worksheet))
      },
    }),
    worksheetDesign: makeAdapter({
      from(response: res.WorksheetDesign) {
        return new WorksheetDesign(response)
      },

      fromDetail(response: res.WorksheetDesignDetail) {
        return new WorksheetDesignOption(response)
      },
    }),
    problem: makeAdapter({
      from(problem: res.MakeWorksheetProblem, nullableOrder: boolean) {
        return new MakeProblem({
          ...problem,
          _concept: problem.concept,
          tagTop: problem.tagTop,
          favorite: problem.favorite ?? false,
          defaultOrder: nullableOrder ? problem.defaultOrder : null,
          sequence: problem.concept.sequence,
          conceptId: problem.concept.id,
          conceptName: problem.concept.name,
          conceptKey: problem.concept.key,
          boxIndex: problem.problemSource?.id ?? null,
        })
      },
    }),
    designTemplateId: {
      from(response: res.WorksheetDetail<'WEAK_WORKSHEET' | 'WORKSHEET'>) {
        if (response.pdf) {
          response.pdf.designTemplateId =
            response.pdf.designTemplateId === null ? EMPTY_NUMBER_ID : response.pdf.designTemplateId
          return response
        }
        return response
      },
      to(request: req.WorksheetDesignDetailUrlOptions) {
        const { designTemplateId, ...req } = request
        return req
      },
    },
    chapters: makeAdapter({
      from(curriculum: CurriculumApi.Response.CurriculumInfo[]) {
        return curriculum.map((chapter) => {
          return {
            curriculumKey: chapter.key,
            curriculumName: chapter.name,
          }
        })
      },
    }),
  }

  private getApi =
    <T extends keyof typeof worksheetApi>(apiName: T) =>
    async (...params: Parameters<ValueOf<Pick<typeof worksheetApi, T>>>) => {
      const {
        data: {
          data: { content, last },
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
      } = await worksheetApi[apiName](...params)
      const coll = this.adapter.worksheetColl.from(content)
      return { coll, last } as { coll: CheckableColl<Worksheet>; last: boolean }
    }

  private getMyWorksheetApi = () => async (params: string) => {
    const {
      data: { content, last },
    } = await worksheetApi.getMyWorksheet(params)
    const coll = this.adapter.worksheetColl.from(content)
    return { coll, last } as { coll: CheckableColl<Worksheet>; last: boolean }
  }

  lessonMaterialFetch = {
    [RouteName.myWorksheet]: this.getMyWorksheetApi(),
  }

  fetchDesignTemplate = async () => {
    const { data } = await worksheetApi.getDesignTemplate()

    return data
  }

  // 학습지 상세 정보
  // TODO: 제네릭 받아서 filter값 추론하도록
  fetchWorksheetDetail = async <T extends 'WEAK_WORKSHEET' | 'WORKSHEET' = 'WORKSHEET'>(
    worksheetId: number,
    ignoredForDeleted?: boolean,
  ) => {
    const { data } = await worksheetApi.getWorksheetDetail<T>(worksheetId, ignoredForDeleted)
    return this.adapter.designTemplateId.from(data)
  }

  getAssignedStudentInfoByWorksheetId = async (id: number) => {
    const {
      data: { data },
    } = await worksheetApi.getAssignedStudentInfoByWorksheetId({ id })
    return data
  }

  // 디자인옵션 리스트
  fetchWorksheetDesign = async (pageQueryString: string) => {
    const {
      data: { content, last },
    } = await worksheetApi.getWorksheetDesign(pageQueryString)

    const worksheetDesignList = content.map(this.adapter.worksheetDesign.from)
    return {
      list: worksheetDesignList,
      last,
    }
  }

  fetchWorksheetDesignDetail = async (id: WorksheetDesign['id']) => {
    const { data } = await worksheetApi.getWorksheetDesignDetail(id)

    return this.adapter.worksheetDesign.fromDetail(data)
  }

  postWorksheetDesignDetail = async (data: req.WorksheetDesignDetailUrlOptions) => {
    await worksheetApi.postWorksheetDesignDetail(this.adapter.designTemplateId.to(data))
  }

  putWorksheetDesignDetail = async (id: number, data: req.WorksheetDesignDetailUrlOptions) => {
    await worksheetApi.putWorksheetDesignDetail(id, this.adapter.designTemplateId.to(data))
  }

  deleteWorksheetDesignDetail = async (id: number) => {
    await worksheetApi.deleteWorksheetDesignDetail(id)
  }

  // 1단계 - 학습지 만들기 : filterId를 통해 학습지 문제 수 조회
  fetchProblemCountByFilterId = async (filterId: string) => {
    const { data: problemCount } = await worksheetApi.getProblemCountByFilterId(
      { filterId },
      {
        useAbortController: true,
      },
    )
    return problemCount
  }

  // 학습지 병합 - filterId 만들기
  createFilterIdByMergedWorksheet = async (params: req.CreateFilterByMerge) => {
    const { data } = await worksheetApi.postFilterIdByMergedWorksheet(params)
    return data.filterId
  }

  // 1단계 - 단원유형별
  // 기존 req.CreateFilterByChapter 쓰이는 곳까지 파악하기 힘들어서 일단 제네릭 처리 ㅜ
  async createFilterIdByChapter<T extends 'Derivation' | 'Worksheet'>(
    params: T extends 'Derivation' ? req.CreateFilterByChapter : 학습지필터.단원유형별필터,
  ) {
    const data = {
      conceptIdList: [],
      excludedTopicIds: [],
      excludedSubTopicIds: [],
      problemList: null,
      selectedConceptIdList: [],
      selectedLittleChapterIdList: [],
      ...params,
      filterId: params.filterId ?? undefined,
    }
    const {
      data: { filterId },
    } = await worksheetApi.createFilterIdByChapter(data, {
      useAbortController: true,
    })
    return filterId
  }

  // 수능 모의고사
  createFilterByPracticeTest = async (params: 학습지필터.수능모의고사필터) => {
    const data = {
      curriculumIdList: null,
      problemList: null,
      selectedConceptIdList: [],
      selectedLittleChapterIdList: [],
      ...params,
      filterId: params.filterId ?? undefined,
    }
    const {
      data: {
        data: { filterId },
      },
    } = await worksheetApi.createFilterByPracticeTest(data)
    return filterId
  }

  createFilterByOneClick = async (data: req.CreateFilterByOneClick) => {
    const {
      data: {
        data: { filterId },
      },
    } = await worksheetApi.createFilterIdByOneClick(data)
    return filterId
  }

  // 2단계 ------

  // UUID를 통한 학습지만들기 필터정보 가지고 오기
  fetchSearchFilterByFilterId = async (filterId: string) => {
    const { data } = await worksheetApi.getSearchFilterByFilterId(filterId, {
      useAbortController: true,
    })

    const option = new MakeWorksheetFilterOption(data)
    // 문제가 있으면 (수정)
    if (data.problemList && data.problemList.length) {
      const problemList = data.problemList.map((problem) =>
        this.adapter.problem.from(problem, true),
      )
      return {
        option,
        makeProblemColl: new MakeProblemColl(CheckableMap.fromArr(problemList, 'id')),
      }
    } else {
      // 문제가 없으면 (1단계 진입) - 유사문제 리스트를 호출해서 채워서 리턴
      const { data: problems } = await worksheetApi.postSearchWorksheetSimilarProblem(
        {
          filterId,
        },
        {
          useAbortController: true,
        },
      )

      const problemList = problems.map((problem) => this.adapter.problem.from(problem, true))

      return {
        option,
        makeProblemColl: new MakeProblemColl(CheckableMap.fromArr(problemList, 'id')),
      }
    }
  }

  // (구) 유사문제, 새문제추가 -> (뉴) 새문제추가만
  fetchSimilarWorksheet = async (params: req.SearchSimilarProblem) => {
    const { data } = await worksheetApi.postSearchWorksheetSimilarProblem(params)
    const problemList = data.map((problem) => this.adapter.problem.from(problem, false))
    return new MakeProblemColl(CheckableMap.fromArr(problemList, 'id'))
  }

  // 3단계
  // 학습지 생성 / 복제후 생성
  postCreateMakeWorksheet = async (data: req.CreateWorksheet) => {
    const studentWorksheetId = await worksheetApi.createWorksheet(data, {
      useAbortController: true,
    })
    return studentWorksheetId
  }

  // 그룹 오답 - 일괄 오답관리 - 학생별 오답학습지
  postGroupWrongWorksheetPerStudent = async (data: req.CreateEachStudentWorksheet) => {
    return await worksheetApi.postGroupWrongWorksheetPerStudent(data)
  }

  // 학습지 수정
  putMakeWorksheet = async (worksheetId: number, data: req.CreateWorksheet) => {
    return await worksheetApi.putWorksheetFix(worksheetId, data)
  }
  // 출제 된 학습지 수정
  patchMakeWorksheet = async (worksheetId: number, data: req.EditAssignedWorksheet) => {
    return await worksheetApi.patchWorksheetFix(worksheetId, data)
  }
  fetchWorksheetDownload = async (
    params: req.WorksheetDownloadParams,
    type?: DownloadWorksheetT['type'],
  ) => {
    const { data } = await worksheetApi.getWorksheetDownload(params, type)
    const { presignedUrl, fileName } = data

    const { fileExt } = splitFilenameAndExtension(fileName)
    return { url: presignedUrl, fullFileName: fileName, fileExt }
  }

  deleteWorksheets = async (worksheetIds: number[]) =>
    await worksheetApi.deleteWorksheets(worksheetIds)

  fetchChapters = async (params: req.CurriculumChapters) => {
    const {
      data: { data },
    } = await worksheetApi.getChapters({
      ...params,
    })
    return this.adapter.chapters.from(data)
  }

  patchAccessModifier = async (requestBody: req.AccessModifier) => {
    await worksheetApi.patchAccessModifier(requestBody)
  }

  getGradeXLS = async (
    { schoolType, grade }: SchoolGradeData,
    dateParams: {
      startYear: number
      endYear: number
      startWeek: number
      endWeek: number
      periodDescription: string
    },
  ) => {
    return worksheetApi.getGradeXLS({ schoolType, grade }, dateParams)
  }

  getClassXLS = async (
    classId: LessonClass['id'],
    dateParams: {
      startYear: number
      endYear: number
      startWeek: number
      endWeek: number
      periodDescription: string
    },
  ) => {
    return worksheetApi.getClassXLS(classId, dateParams)
  }
}

export const worksheetController = new WorksheetController()
